<!--
  TeamPermissionsPage -- Distributor App Account section, Team & Permissions.

  Card: Distributor Onboarding & Account (https://trello.com/c/QV2U4KhF)
  Pebble: Account section pages -- Team & Permissions and Payments
  (replacing placeholder frames).

  Replaces the DistributorComingSoon frame at /distributor/account/team.

  This is a THIN composition over already-built, already-tested pieces:
    - TeamMembersPanel.vue              (real members table + invite/delete
      affordances; owns no data, emits events)
    - DeleteTeammateDialog.vue          (deactivate teammate -- ADR 0021
      "remove them entirely" branch)
    - HandOffOwnershipDialog.vue        (ADR 0021 "Hand off ownership"
      single-button Owner -> Builder transfer)

  Polish item 2026-06-04: the invite flow moved from an inline
  v-dialog on this page to a sub-route at
  /distributor/account/team/invite (InviteTeammatePage). The invite
  affordances on this page now navigate via $router.push instead of
  opening a modal. The invite dialog itself is unchanged -- only its
  host shell changed.

  The subscription / teammate data layer lives in the shared
  useTeamSubscription composable so this page does NOT copy-paste the
  monolithic logic still embedded in the legacy DistributorAccountInfo.vue
  and DistributorAddEdit.vue. The composable owns the axios calls + reactive
  state; the page wires events to its handlers.
-->
<template>
  <v-container fluid data-testid="dist-account-team-page">
    <v-row>
      <v-col cols="12">
        <div class="d-flex align-center">
          <h1 class="md-header">Team &amp; Permissions</h1>
          <HelpIcon
            audience="owner"
            slug="set-up-your-business"
            heading="team-permissions"
          />
          <v-spacer />
          <button
            type="button"
            class="tf-btn tf-btn--primary"
            data-testid="team-invite-btn"
            :disabled="loading"
            @click="goToInvitePage"
          >
            Invite Teammate
          </button>
          <!--
            ADR 0021 Hand-off Ownership is built and wired on the frontend,
            but the atomic Owner-transfer backend endpoint
            (/subscription/transferOwnership/) does not exist yet -- only the
            distributor.team.transfer_ownership capability is defined. Ship the
            affordance DISABLED so we never expose a path that 404s at runtime.
            Re-enable by dropping handOffDisabled once the backend route lands
            (tracked as a follow-up issue).

            The coming-soon signal is scoped to THIS control (disabled state +
            title hint). It must NOT be widened into a page-level banner --
            see the MockDataBanner note below the header.
          -->
          <button
            type="button"
            class="tf-btn tf-btn--secondary ml-2"
            data-testid="team-handoff-ownership-btn"
            :disabled="loading || handOffDisabled"
            :title="
              handOffDisabled
                ? 'Hand off ownership ships shortly after beta launch.'
                : undefined
            "
            @click="openHandOffDialog"
          >
            Hand Off Ownership
          </button>
        </div>
        <!--
          Card Fzkq5GxA: the MockDataBanner ("Hand off ownership is showing
          example data ... Live data ships in a follow-on PR") is GONE -- this
          page shows LIVE data. Its members table sources from the real
          /distributor/teamMembers API (see the region below), so a page-level
          DEMO DATA banner told users their real team was fake.

          The banner was only ever standing in for the deferred Hand-Off-
          Ownership control. That feature is still unshipped, so the control
          above stays disabled with its own title hint -- but a disabled
          BUTTON is the honest signal, not a banner impeaching the page's data.
          Do not reintroduce a MockDataBanner here while the members table is
          live.
        -->

        <v-progress-circular
          v-if="loading"
          :indeterminate="true"
          color="primary"
          size="32"
          width="4"
          class="mt-4"
        />

        <!--
          PR-1/4 (card IlgSKtZl): the members table now sources from real
          member ACCOUNTS (impersonation-aware) via GET /distributor/teamMembers,
          replacing the legacy Stripe-subscription roster
          (the per-seat subscription panel, since deleted).
        -->
        <div data-testid="team-members-region" class="mt-4">
          <TeamMembersPanel
            :members="teamMembers.members.value"
            @members-changed="teamMembers.load()"
          />
        </div>
      </v-col>
    </v-row>

    <DeleteTeammateDialog
      ref="deleteDialog"
      @delete-teammate="onDeleteTeammate"
    />

    <HandOffOwnershipDialog
      ref="handOffDialog"
      :recipients="eligibleRecipients"
      @transfer-ownership="onTransferOwnership"
    />
  </v-container>
</template>

<script>
import { computed, onMounted } from "vue";
import TeamMembersPanel from "./TeamMembersPanel.vue";
import DeleteTeammateDialog from "./DeleteTeammateDialog.vue";
import HandOffOwnershipDialog from "./HandOffOwnershipDialog.vue";
import { useTeamSubscription } from "@/composables/useTeamSubscription";
import { useTeamMembers } from "@/composables/useTeamMembers";
import { useAccountStore } from "@/stores/account";
import axios from "@/axios";
import store from "@/store";
import HelpIcon from "@/components/Help/HelpIcon.vue";

export default {
  name: "TeamPermissionsPage",

  components: {
    HelpIcon,
    TeamMembersPanel,
    DeleteTeammateDialog,
    HandOffOwnershipDialog,
  },

  props: {
    // Optional explicit distributor id so the page is mount-testable
    // without a live store. Resolved from the account store otherwise.
    distributorId: {
      type: [Number, String],
      default: null,
    },
  },

  setup(props) {
    let resolvedId = props.distributorId;
    if (resolvedId === null || resolvedId === "") {
      try {
        const account = useAccountStore();
        resolvedId = account.distributor ? account.distributor.id : null;
      } catch (e) {
        resolvedId = null;
      }
    }

    // The composable owns the axios layer + reactive state; the shared
    // axios instance and legacy Vuex store are injected so the page does
    // not reach through `this`. The spec replaces useTeamSubscription
    // wholesale, so these args are inert under test.
    const team = useTeamSubscription({
      axios,
      store,
      distributorId: resolvedId,
    });

    // PR-1/4 (card IlgSKtZl): the members table sources from real member
    // ACCOUNTS via GET /distributor/teamMembers (impersonation-aware; no
    // distributor id in the path). The subscription composable above is
    // retained for the deferred hand-off recipient list only.
    const teamMembers = useTeamMembers({ axios });

    // The composable returns refs (via toRefs) + computed refs. Destructure
    // them so they pass through setup-return unwrapped -- wrapping a ref in
    // `computed(() => team.subscription)` would yield the ref OBJECT, not its
    // value, because `team` is a plain object (not reactive) and only refs
    // that are properties of a REACTIVE object auto-unwrap when read.
    const {
      subscription,
      accounts,
      userHasSubscription,
      loading,
      sendingEmail,
      totalSubscriptionPrice,
      distributorEmail,
    } = team;

    // ADR 0021 Hand-off Ownership: the affordance is built + wired, but the
    // atomic Owner-transfer endpoint (POST /subscription/transferOwnership/)
    // does not exist on the backend yet -- only the
    // distributor.team.transfer_ownership capability is defined. Ship the
    // button DISABLED so we never expose a path that 404s at runtime. Flip to
    // false once the backend route lands (tracked as a follow-up issue).
    const handOffDisabled = true;

    // ADR 0021: eligible hand-off recipients are "any current Builder" --
    // active, non-Owner, non-invited teammates.
    const eligibleRecipients = computed(() =>
      accounts.value.filter(
        (a) =>
          a.active &&
          a.status !== "invited" &&
          a.status !== "canceled" &&
          a.email !== distributorEmail.value
      )
    );

    onMounted(() => {
      // Load the real members roster for this page's table. The legacy
      // subscription load is still invoked so the deferred hand-off dialog's
      // recipient list (eligibleRecipients) has data when it is eventually
      // enabled; it no longer feeds the members table.
      teamMembers.load();
      team.load();
    });

    return {
      team,
      teamMembers,
      subscription,
      accounts,
      userHasSubscription,
      loading,
      sendingEmail,
      totalSubscriptionPrice,
      distributorEmail,
      eligibleRecipients,
      handOffDisabled,
    };
  },

  methods: {
    // Polish item 2026-06-04: the invite flow is now a sub-route.
    // Header button + panel `invite-teammate` event both navigate to
    // /distributor/account/team/invite (InviteTeammatePage); the
    // deferred guard there owns dirty-form prompting.
    goToInvitePage() {
      this.$router.push("/distributor/account/team/invite");
    },
    openHandOffDialog() {
      this.$refs.handOffDialog.show();
    },
    openDeleteDialog(item) {
      this.$refs.deleteDialog.show(item);
    },
    async onDeleteTeammate(teammate) {
      await this.team.deleteTeammate(teammate);
    },
    async onResendInvitations() {
      await this.team.resendInvitations();
    },
    async onTransferOwnership(recipient) {
      await this.team.transferOwnership(recipient);
    },
  },
};
</script>

<style scoped>
.md-header {
  font-size: var(--tf-size-h2, 1.5rem);
  font-weight: 600;
}
.tf-btn {
  border: none;
  border-radius: var(--tf-radius-sm, 8px);
  padding: var(--tf-sp-2, 8px) var(--tf-sp-5, 20px);
  font-size: var(--tf-size-body, 1rem);
  cursor: pointer;
}
.tf-btn:disabled {
  opacity: 0.6;
  cursor: default;
}
.tf-btn--primary {
  background: var(--tf-primary, var(--tf-primary));
  color: var(--tf-fg-on-primary, var(--tf-bg));
}
.tf-btn--secondary {
  background: var(--tf-surface-alt, var(--tf-surface-alt));
  color: var(--tf-fg-1, var(--tf-fg-1));
}
</style>
